import { usePostTablesGlobalAttributes } from '@ipfabric/api-client/client/model/attributes/attributes';
import { usePostTablesSettingsJumphosts } from '@ipfabric/api-client/client/model/jumphosts/jumphosts';
import { usePostTablesReportsSettingsOui } from '@ipfabric/api-client/client/model/reports/reports';
import { plural } from '@ipfabric/frontend-shared-assets/utils/plural';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import {
  TabbedTables,
  type TableTabRoute,
} from 'components/TabbedTables/TabbedTables';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { createTableApiScopeRequirement } from 'utils/rbac/helpers/createTableApiScopeRequirement';
import { DeviceAttributesPage } from './DeviceAttributes/DeviceAttributesPage';
import { Jumphost } from './Jumphost/Jumphost';
import { OuiPage } from './Oui/OuiPage';

const deviceAttributesUrl =
  '/settings/discovery-snapshots/global-discovery-configuration/device-attributes';
const jumphostUrl =
  '/settings/discovery-snapshots/global-discovery-configuration/jumphost';
const ouiUrl =
  '/settings/discovery-snapshots/global-discovery-configuration/oui';

export const globalDiscoveryConfigurationdRoutes: TableTabRoute[] = [
  {
    path: deviceAttributesUrl,
    element: <DeviceAttributesPage />,
    getTabLabel: (msg) =>
      msg.settings.globalDiscoveryConfiguration.tabs.deviceAttributes,
    requiredApiScope: createTableApiScopeRequirement(
      '/tables/global-attributes',
    ),
  },
  {
    path: jumphostUrl,
    element: <Jumphost />,
    getTabLabel: (msg) =>
      msg.settings.globalDiscoveryConfiguration.tabs.jumphost,
    requiredApiScope: createTableApiScopeRequirement(
      '/tables/settings/jumphosts',
    ),
  },
  {
    path: ouiUrl,
    element: <OuiPage />,
    getTabLabel: (msg) => msg.settings.globalDiscoveryConfiguration.tabs.oui,
    requiredApiScope: createTableApiScopeRequirement(
      '/tables/reports/settings-oui',
    ),
  },
];

export const GlobalDiscoveryConfiguration: FC = () => {
  const configMsg = useLocale().settings.globalDiscoveryConfiguration;

  const attrCount = usePostTablesGlobalAttributes({
    columns: ['id'],
    pagination: { limit: 0 },
  }).data?._meta.count;

  const ouiCount = usePostTablesReportsSettingsOui({
    columns: ['id'],
    pagination: { limit: 0 },
  }).data?._meta.count;

  const jumphostCount = usePostTablesSettingsJumphosts({
    columns: ['id'],
    pagination: { limit: 0 },
  }).data?._meta.count;

  const sideMenuDescription = useMemo(
    () => ({
      [deviceAttributesUrl]: `${attrCount ?? '?'} ${plural(attrCount ?? 0, 'attribute')}`,
      [jumphostUrl]: `${jumphostCount ?? '?'} ${plural(jumphostCount ?? 0, 'entry', 'entries')}`,
      [ouiUrl]: `${ouiCount ?? '?'} ${plural(ouiCount ?? 0, 'entry', 'entries')}`,
    }),
    [attrCount, ouiCount, jumphostCount],
  );

  return (
    <>
      <Helmet title={configMsg.title} />
      <PageHeader width='full' label='Global configuration' />

      <TabbedTables
        className='m-4'
        layout='side'
        sideMenuDescription={sideMenuDescription}
        tabs={globalDiscoveryConfigurationdRoutes}
        rootPath='/settings/discovery-snapshots/global-discovery-configuration'
      />
    </>
  );
};
